import { cloneDeep } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { AutoPricingCircuitBreakerMaxDiscountVelocityInput } from 'src/components/AutoPricing/AutoPricingCircuitBreakerMaxDiscountVelocityInput/AutoPricingCircuitBreakerMaxDiscountVelocityInput';
import { AutoPricingCompRowOffsetModeSelector } from 'src/components/AutoPricing/AutoPricingCompRowOffsetModeSelector/AutoPricingCompRowOffsetModeSelector';
import { AutoPricingDefaultCompQuantitiesInput } from 'src/components/AutoPricing/AutoPricingDefaultCompQuantitiesInput/AutoPricingDefaultCompQuantitiesInput';
import { AutoPricingMinCompListingsSlider } from 'src/components/AutoPricing/AutoPricingMinCompListingsSlider/AutoPricingMinCompListingsSlider';
import {
  AutoPricingSettingsFormContent,
  PricingSetting,
} from 'src/components/AutoPricing/AutoPricingSettingsFormContent/AutoPricingSettingsFormContent';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack, Switch, Tooltip } from 'src/core/ui';
import { Button } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { InfoOutlineIcon } from 'src/svgs/Viagogo/functional-svgs/InfoOutline';
import { ContentId } from 'src/utils/constants/contentId';
import {
  AUTOPRICING_COMPFAILUREACTIONTYPE_TO_CID,
  AUTOPRICING_PAUSE_TYPE_TO_CID,
  AUTOPRICING_PAUSE_TYPE_TO_TOOLTIP_CID,
} from 'src/utils/constants/contentIdMaps';
import {
  AccountAutoPricingSettingsInputs,
  AutoPricingCompFailureActionType,
  AutoPricingPauseType,
  Feature,
  UiCurrency,
} from 'src/WebApiController';

import { setValueAsDecimalNumber } from '../../SyncCenter/SyncCenter.utils';
import * as styles from './AccountAutoPricingSettingsForm.css';
import { useYourListingsInCompsSetting } from './AccountAutoPricingSettingsForm.hooks';

export const AccountAutoPricingSettingsForm = ({
  pricingSettings,
  onSaved,
  uiCurrency,
  disabled: disabledProp,
  allowSeatScoreCompListingMode,
}: {
  pricingSettings: AccountAutoPricingSettingsInputs | null | undefined;
  onSaved?: (
    pricingSettingsNew: AccountAutoPricingSettingsInputs
  ) => Promise<void>;
  uiCurrency: UiCurrency;
  disabled: boolean;
  allowSeatScoreCompListingMode: boolean;
}) => {
  const hasCompRowOffsetModeFeature = useUserHasFeature(
    Feature.AutoPricingCompRowOffsetMode
  );
  const hasCompFailureActionFeature = useUserHasFeature(
    Feature.AutoPricingCompFailureAction
  );
  const methods = useForm<AccountAutoPricingSettingsInputs>({
    defaultValues: pricingSettings ? cloneDeep(pricingSettings) : undefined,
  });

  const {
    enabled,
    showUnbroadcastListingsInComps,
    setShowUnbroadcastListingsInComps,
    onSaveShowUnbroadcastListingsInCompsSetting,
  } = useYourListingsInCompsSetting();

  const onSettingsSaved = useCallback(
    async (data: AccountAutoPricingSettingsInputs) => {
      await onSaved?.(data);
      onSaveShowUnbroadcastListingsInCompsSetting();
    },
    [onSaveShowUnbroadcastListingsInCompsSetting, onSaved]
  );

  const { handleSubmit } = methods;

  const disabled = disabledProp;
  return (
    <FormProvider {...methods}>
      <AutoPricingSettingsFormContent
        venueMapSections={undefined}
        hasSeatScores={false}
        uiCurrency={uiCurrency}
        allowSeatScoreCompListingMode={allowSeatScoreCompListingMode}
        hideCompListingQuantityFilters={true}
        hideUnderCutAmountInput={false}
        disabled={disabled}
        priceAgainstSectionChildren={
          <>
            {hasCompRowOffsetModeFeature && (
              <PricingSetting
                header={<Content id={ContentId.MatchRowBy} />}
                detail={
                  <AutoPricingCompRowOffsetModeSelector disabled={disabled} />
                }
              />
            )}

            <PricingSetting
              header={
                <div>
                  <Content id={ContentId.DefaultQuantityFilters} />
                </div>
              }
              headerStyle={{ alignSelf: 'start' }}
              detail={
                <AutoPricingDefaultCompQuantitiesInput disabled={disabled} />
              }
            />

            {!hasCompFailureActionFeature && (
              <PricingSetting
                header={
                  <div>
                    <Content id={ContentId.MinListingsRequired} />
                  </div>
                }
                detail={
                  <AutoPricingMinCompListingsSlider disabled={disabled} />
                }
              />
            )}
          </>
        }
        pricingProtectionSectionChildren={
          <PricingProtectionSettings disabled={disabled} />
        }
        advancedSettingsSectionChildren={
          <>
            <VelocityCircuitBreakerSettings disabled={disabled} />
            {enabled && (
              <ShowUnbroadcastListingsInComps
                disabled={disabled}
                showUnbroadcastListingsInComps={Boolean(
                  showUnbroadcastListingsInComps
                )}
                setShowUnbroadcastListingsInComps={
                  setShowUnbroadcastListingsInComps
                }
              />
            )}
          </>
        }
      />

      <div className={styles.footer}>
        <Button disabled={disabled} onClick={handleSubmit(onSettingsSaved)}>
          <Content id={ContentId.Save} />
        </Button>
      </div>
    </FormProvider>
  );
};

const PricingProtectionSettings = ({ disabled }: { disabled: boolean }) => {
  const { isDarkMode } = useSiteTheme();

  const hasCompFailureActionFeature = useUserHasFeature(
    Feature.AutoPricingCompFailureAction
  );
  const hasAutoPricingPausePriceUpdatesFeature = useUserHasFeature(
    Feature.AutoPricingPausePriceUpdates
  );
  const { watch, setValue, register } =
    useFormContext<AccountAutoPricingSettingsInputs>();

  const compFailureActionType = watch('compFailureActionType');
  const setPriceToCeilingTooltipText = useContent(
    ContentId.SetPriceToCeilingTooltipText
  );
  const compFailureActionTooltipText = useMemo(() => {
    return compFailureActionType ===
      AutoPricingCompFailureActionType.SetPriceToCeiling
      ? setPriceToCeilingTooltipText
      : undefined;
  }, [compFailureActionType, setPriceToCeilingTooltipText]);

  const pricePauseType = watch('autoPricingPauseType');

  const [priceUpdatesPaused, setPriceUpdatesPaused] = useState(
    pricePauseType != null
  );

  const pauseTypeTooltipText = useContent(
    pricePauseType != null
      ? AUTOPRICING_PAUSE_TYPE_TO_TOOLTIP_CID[pricePauseType]
      : undefined
  );

  return (
    <Stack gap="l" direction="column">
      {hasCompFailureActionFeature && (
        <>
          <PricingSetting
            header={
              <div>
                <Content id={ContentId.MinComparablesRequired} />
              </div>
            }
            detail={<AutoPricingMinCompListingsSlider disabled={disabled} />}
          />

          <PricingSetting
            header={<Content id={ContentId.OnInsufficientComparables} />}
            detail={
              <Stack gap="m" alignItems="center" direction="row">
                <PosEnumSelect
                  disabled={disabled}
                  value={
                    compFailureActionType ??
                    AutoPricingCompFailureActionType.DontChangePrice
                  }
                  valueOptionsContent={AUTOPRICING_COMPFAILUREACTIONTYPE_TO_CID}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(v) => {
                    setValue(
                      'compFailureActionType',
                      v as AutoPricingCompFailureActionType
                    );
                  }}
                />
                {compFailureActionTooltipText && (
                  <Tooltip
                    triggerContent={
                      <div style={{ display: 'flex' }}>
                        <InfoOutlineIcon size={vars.iconSize.m} />
                      </div>
                    }
                    contentVariant={isDarkMode ? 'light' : 'dark'}
                  >
                    <div>{compFailureActionTooltipText}</div>
                  </Tooltip>
                )}
              </Stack>
            }
          />
        </>
      )}

      <PricingSetting
        header={<Content id={ContentId.FloorRelativeToUnitCost} />}
        detail={
          <PosTextField
            disabled={disabled}
            type="number"
            postfixDisplay="%"
            rootProps={{
              className: styles.inputInline,
            }}
            className={styles.inputInline}
            {...register('circuitBreakerRelativeFloor', {
              setValueAs: setValueAsDecimalNumber,
            })}
          />
        }
      />

      {hasAutoPricingPausePriceUpdatesFeature && (
        <>
          <PricingSetting
            header={<Content id={ContentId.PauseAutoPricing} />}
            detail={
              <Switch
                disabled={disabled}
                checked={priceUpdatesPaused}
                onClick={(e) => e.stopPropagation()}
                onCheckedChange={(isChecked) => {
                  if (!isChecked) {
                    setValue('autoPricingPauseType', null);
                    setPriceUpdatesPaused(false);
                  } else {
                    setPriceUpdatesPaused(true);
                  }
                }}
              />
            }
          />
          {priceUpdatesPaused && (
            <PricingSetting
              header={<Content id={ContentId.PauseType} />}
              detail={
                <Stack gap="m" alignItems="center" direction="row">
                  <PosEnumSelect
                    disabled={disabled}
                    value={pricePauseType}
                    valueOptionsContent={AUTOPRICING_PAUSE_TYPE_TO_CID}
                    placeholderText={ContentId.None}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(v) =>
                      setValue(
                        'autoPricingPauseType',
                        v as AutoPricingPauseType
                      )
                    }
                    sortFn={(a, b) => {
                      const order = {
                        [AutoPricingPauseType.PriceDecreases]: 1,
                        [AutoPricingPauseType.AllPriceUpdates]: 2,
                      };
                      return (
                        (order[a as AutoPricingPauseType] ?? 999) -
                        (order[b as AutoPricingPauseType] ?? 999)
                      );
                    }}
                  />
                  {pauseTypeTooltipText && (
                    <Tooltip
                      triggerContent={
                        <div style={{ display: 'flex' }}>
                          <InfoOutlineIcon size={vars.iconSize.m} />
                        </div>
                      }
                      contentVariant={isDarkMode ? 'light' : 'dark'}
                    >
                      <div>{pauseTypeTooltipText}</div>
                    </Tooltip>
                  )}
                </Stack>
              }
            />
          )}
        </>
      )}
    </Stack>
  );
};

const VelocityCircuitBreakerSettings = ({
  disabled,
}: {
  disabled: boolean;
}) => {
  const { setValue, watch } =
    useFormContext<AccountAutoPricingSettingsInputs>();

  const {
    circuitBreakerMaxDiscountVelocityPercent,
    circuitBreakerMaxDiscountVelocityTicksInHours,
  } = watch();

  const restrictVelocityOfPriceUpdates = useMemo(() => {
    return (
      !!circuitBreakerMaxDiscountVelocityPercent &&
      !!circuitBreakerMaxDiscountVelocityTicksInHours
    );
  }, [
    circuitBreakerMaxDiscountVelocityPercent,
    circuitBreakerMaxDiscountVelocityTicksInHours,
  ]);

  return (
    <>
      <PricingSetting
        header={<Content id={ContentId.RestrictPriceDropVelocity} />}
        detail={
          <Switch
            disabled={disabled}
            checked={restrictVelocityOfPriceUpdates}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(isChecked) => {
              const restrictVelocityOfPriceUpdatesNew = isChecked;
              if (
                restrictVelocityOfPriceUpdatesNew !==
                restrictVelocityOfPriceUpdates
              ) {
                const circuitBreakerMaxDiscountVelocityPercentNew =
                  restrictVelocityOfPriceUpdatesNew ? 0.8 : null;
                const circuitBreakerMaxDiscountVelocityTicksInHours =
                  restrictVelocityOfPriceUpdatesNew ? 1 : null;
                setValue(
                  'circuitBreakerMaxDiscountVelocityPercent',
                  circuitBreakerMaxDiscountVelocityPercentNew
                );
                setValue(
                  'circuitBreakerMaxDiscountVelocityTicksInHours',
                  circuitBreakerMaxDiscountVelocityTicksInHours
                );
              }
            }}
          />
        }
      />

      {restrictVelocityOfPriceUpdates && (
        <PricingSetting
          header={<Content id={ContentId.Velocity} />}
          detail={
            <AutoPricingCircuitBreakerMaxDiscountVelocityInput
              disabled={disabled}
            />
          }
        />
      )}
    </>
  );
};

const ShowUnbroadcastListingsInComps = ({
  disabled,
  showUnbroadcastListingsInComps,
  setShowUnbroadcastListingsInComps,
}: {
  disabled: boolean;
  showUnbroadcastListingsInComps: boolean;
  setShowUnbroadcastListingsInComps: (_: boolean) => void;
}) => {
  return (
    <PricingSetting
      header={<Content id={ContentId.ShowUnbroadcastListingsInComps} />}
      headerStyle={{ width: '200px' }} // Fixed width to wrap the long header title
      detail={
        <Switch
          disabled={disabled}
          checked={showUnbroadcastListingsInComps}
          onClick={(e) => e.stopPropagation()}
          onCheckedChange={setShowUnbroadcastListingsInComps}
        />
      }
    />
  );
};
